import { useQuery } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { fetchAuctionSessions } from './api';
import { LegacyAuctionBoard } from './components/legacy-board';
import { SessionCalendarView } from './components/session-schedule/session-calendar-view';
import { SessionDayPanel } from './components/session-schedule/session-day-panel';
import { SessionFilterBar } from './components/session-schedule/session-filter-bar';
import { SessionListView } from './components/session-schedule/session-list-view';
import { SessionStatCards } from './components/session-schedule/session-stat-cards';
import { SessionTimelineView } from './components/session-schedule/session-timeline-view';
import { SessionViewToggle } from './components/session-schedule/session-view-toggle';
import type {
  ScheduleHouseChip,
  ScheduleScreen,
  ScheduleSessionView,
  ScheduleTimelineCell,
} from './components/session-schedule/session-schedule-types';
import {
  buildMonthGrid,
  buildTimelineDays,
  dayDiff,
  formatClock,
  formatDayMonth,
  formatMonthLabel,
  formatPanelTitle,
  groupByDay,
  houseColor,
  lotsLabel,
  sessionChipClasses,
  sessionDotColor,
  sessionStatus,
  sessionWindow,
  sessionsLabel,
  statusLabel,
  todayInDisplayZone,
  viewsOnDay,
  weekdayHeadings,
} from './components/session-schedule/session-schedule-utils';
import { cn, isAccessDeniedError } from './utils';

/**
 * Auction schedule — top-level shell for `/auction-schedule`.
 *
 * Owns the extended four-tab switch this phase introduces (brief point 3):
 * the three Design D session views (calendar / list / timeline, fed by
 * `GET /auction-sessions` — the same window-based feed the web auction app's
 * `AuctionSchedulePage` consumes) plus the ORIGINAL per-lot bid-relay board
 * (`./components/legacy-board.tsx`, fed by the admin-only
 * `GET /admin/auction-schedule`) as its fourth tab. The board's own file was
 * not touched beyond being extracted out of this one — see its header
 * comment. Default tab is `calendar` (brief point 4); the board keeps
 * whatever state it owned before (defaults to its own `list`).
 *
 * `myBidsOnly` from the web original does not exist here anywhere: the admin
 * operator's session views never render "your bids" — see the header
 * comments on `./components/session-schedule/session-day-panel.tsx` and
 * `./components/session-schedule/session-filter-bar.tsx` for where that was
 * dropped and why.
 */
export function AuctionSchedulePage() {
  const [screen, setScreen] = useState<ScheduleScreen>('calendar');
  const today = useMemo(() => todayInDisplayZone(), []);
  const [selectedDay, setSelectedDay] = useState(today);
  const [cursor, setCursor] = useState(() => ({
    year: Number(today.slice(0, 4)),
    month: Number(today.slice(5, 7)) - 1,
  }));
  const [houseFilter, setHouseFilter] = useState<string[]>([]);

  const range = useMemo(() => sessionWindow(cursor.year, cursor.month, today), [cursor, today]);

  const sessionsQuery = useQuery({
    queryKey: ['auction-sessions', range],
    queryFn: () => fetchAuctionSessions(range),
    // The legacy board fetches its own data; skip the session-schedule window
    // fetch while that tab is open rather than racing two queries.
    enabled: screen !== 'board',
  });

  const sessions = useMemo(() => sessionsQuery.data?.data ?? [], [sessionsQuery.data]);
  const houses = useMemo(() => sessionsQuery.data?.houses ?? [], [sessionsQuery.data]);

  const houseColors = useMemo(() => {
    const map = new Map<string, string>();
    houses.forEach((house, index) => map.set(house.key, houseColor(index)));
    return map;
  }, [houses]);

  /**
   * EVERY session in the fetched window, decorated. The house filter is applied
   * on top of this rather than inside it, because the two readerships differ:
   * the three session views read the FILTERED `views`, while the stat cards
   * read this unfiltered list — see the comment on `sessionsThisMonth` below.
   */
  const allViews: ScheduleSessionView[] = useMemo(() => {
    const now = new Date();

    return sessions.map((session) => {
      const status = sessionStatus(session, today, now);
      return {
        session,
        status,
        dot: sessionDotColor(status, houseColors.get(session.house.key) ?? houseColor(0)),
        chip: sessionChipClasses(status),
      };
    });
  }, [sessions, today, houseColors]);

  const views: ScheduleSessionView[] = useMemo(
    () =>
      allViews.filter((item) => houseFilter.length === 0 || houseFilter.includes(item.session.house.key)),
    [allViews, houseFilter],
  );

  const houseChips: ScheduleHouseChip[] = useMemo(
    () =>
      houses.map((house, index) => ({
        house,
        color: houseColor(index),
        selected: houseFilter.includes(house.key),
      })),
    [houses, houseFilter],
  );

  const monthPrefix = `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}`;
  const cells = useMemo(() => buildMonthGrid(cursor.year, cursor.month), [cursor]);
  const weekdays = useMemo(() => weekdayHeadings(), []);
  const monthLabel = formatMonthLabel(cursor.year, cursor.month);

  const selectedViews = useMemo(() => viewsOnDay(views, selectedDay), [views, selectedDay]);

  const listDays = useMemo(
    () => groupByDay(views.filter((item) => item.session.date.startsWith(monthPrefix) && item.status !== 'past')),
    [views, monthPrefix],
  );

  const timelineDays = useMemo(() => buildTimelineDays(today), [today]);

  const timelineHouses = useMemo(
    () => houseChips.filter((chip) => houseFilter.length === 0 || houseFilter.includes(chip.house.key)),
    [houseChips, houseFilter],
  );

  const timelineCells = useMemo(() => {
    const map = new Map<string, ScheduleTimelineCell>();

    for (const item of views) {
      const key = `${item.session.house.key}|${item.session.date}`;
      const existing = map.get(key);

      if (!existing) {
        map.set(key, { lots: item.session.lots, time: item.session.time, dot: item.dot, chip: item.chip });
        continue;
      }

      existing.lots += item.session.lots;
      if (item.session.time < existing.time) {
        existing.time = item.session.time;
        existing.dot = item.dot;
        existing.chip = item.chip;
      }
    }

    return map;
  }, [views]);

  // Both of these feed the stat cards, so they read `allViews`, NOT `views`.
  // The other four card values come from server `stats`, which is computed over
  // the whole window with no house filter; deriving these two from the filtered
  // list made a filtered screen show one house's session count next to both
  // houses' lot counts, as one picture. The block is uniformly window-scoped;
  // the house filter scopes the views below it.
  const sessionsThisMonth = useMemo(
    () => allViews.filter((item) => item.session.date.startsWith(monthPrefix)).length,
    [allViews, monthPrefix],
  );

  const nextSessionToday = useMemo(() => {
    const todaySessions = allViews
      .filter((item) => item.session.date === today && item.status !== 'past')
      .map((item) => item.session)
      .sort((left, right) => left.time.localeCompare(right.time));

    return todaySessions[0] ?? null;
  }, [allViews, today]);

  const nextAfterSelected = useMemo(
    () =>
      views
        .filter((item) => item.session.date > selectedDay)
        .map((item) => item.session)
        .sort((left, right) => `${left.date}${left.time}`.localeCompare(`${right.date}${right.time}`))[0] ?? null,
    [views, selectedDay],
  );

  const selectedOffset = dayDiff(today, selectedDay);
  const panelEyebrow =
    selectedOffset === 0
      ? t('auctionSchedule.day.today')
      : selectedOffset === 1
        ? t('auctionSchedule.day.tomorrow')
        : selectedOffset < 0
          ? t('auctionSchedule.day.past')
          : t('auctionSchedule.day.inDays', { days: selectedOffset });

  const panelSubtitle = (() => {
    if (selectedViews.length === 0) return t('auctionSchedule.day.free');
    const lots = selectedViews.reduce((total, item) => total + item.session.lots, 0);
    return `${sessionsLabel(selectedViews.length, t)} · ${lotsLabel(lots, t)}`;
  })();

  const nextHint = nextAfterSelected
    ? t('auctionSchedule.day.nextHint', {
        date: formatDayMonth(nextAfterSelected.date),
        house: nextAfterSelected.house.name,
        time: nextAfterSelected.time,
      })
    : t('auctionSchedule.day.nextHintNone');

  const handleViewChange = useCallback((next: ScheduleScreen) => setScreen(next), []);

  const handleSelectDay = useCallback((date: string) => setSelectedDay(date), []);

  const shiftMonth = useCallback((delta: number) => {
    setCursor((previous) => {
      const next = new Date(Date.UTC(previous.year, previous.month + delta, 1));
      return { year: next.getUTCFullYear(), month: next.getUTCMonth() };
    });
  }, []);

  const handleToday = useCallback(() => {
    setCursor({ year: Number(today.slice(0, 4)), month: Number(today.slice(5, 7)) - 1 });
    setSelectedDay(today);
  }, [today]);

  const handleRefresh = useCallback(async () => {
    const result = await sessionsQuery.refetch();
    if (result.isError) {
      console.error('Failed to refresh auction sessions:', result.error);
      toast.error(t('auctionSchedule.messages.error'));
      return;
    }
    toast.success(t('auctionSchedule.messages.refreshed'));
  }, [sessionsQuery]);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SessionViewToggle currentScreen={screen} onScreenChange={handleViewChange} />
        {screen !== 'board' && <SessionFilterBar houses={houseChips} onToggleHouse={toggleHouse(setHouseFilter)} />}
      </div>

      {screen === 'board' && <LegacyAuctionBoard />}

      {screen !== 'board' && (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
                {t('auctionSchedule.title')}
              </h1>
              <p className="mt-2 font-mono text-[11px] tracking-[0.14em] text-cf-ink-subtle uppercase">
                {sessionsQuery.data?.lastUpdated &&
                  t('auctionSchedule.updatedAt', { time: formatClock(new Date(sessionsQuery.data.lastUpdated)) })}
              </p>
            </div>

            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={sessionsQuery.isFetching}
              className={cn(
                'inline-flex h-10 items-center gap-2 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink disabled:opacity-50',
              )}
            >
              <RefreshCw size={16} strokeWidth={1.75} className={sessionsQuery.isFetching ? 'animate-spin' : ''} />
              {t('auctionSchedule.actions.refresh')}
            </button>
          </div>

          {sessionsQuery.isError && isAccessDeniedError(sessionsQuery.error) && <AccessDenied />}

          {sessionsQuery.isError && !isAccessDeniedError(sessionsQuery.error) && (
            <div className="rounded-cf-lg border border-cf-line bg-cf-surface p-10 text-center">
              <p className="text-[18px] font-semibold">{t('auctionSchedule.messages.error')}</p>
              <button
                type="button"
                onClick={() => void sessionsQuery.refetch()}
                className="mt-4 h-10 cursor-pointer rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover"
              >
                {t('auctionSchedule.actions.refresh')}
              </button>
            </div>
          )}

          {!sessionsQuery.isError && (
            <>
              <SessionStatCards
                monthLabel={monthLabel}
                sessionsThisMonth={sessionsThisMonth}
                activeLots={sessionsQuery.data?.stats.activeLots ?? 0}
                lotsToday={sessionsQuery.data?.stats.lotsToday ?? 0}
                nextSessionToday={nextSessionToday}
                lotsThisWeek={sessionsQuery.data?.stats.lotsThisWeek ?? 0}
                sessionsThisWeek={sessionsQuery.data?.stats.sessionsThisWeek ?? 0}
                onShowList={() => handleViewChange('list')}
                onShowToday={() => {
                  handleToday();
                  handleViewChange('calendar');
                }}
                onShowTimeline={() => handleViewChange('timeline')}
              />

              <div
                className={cn(sessionsQuery.isFetching && !sessionsQuery.isLoading && 'opacity-60')}
                aria-busy={sessionsQuery.isFetching}
              >
                {/*
                  First load renders a loading panel, not the views: `sessions`
                  is [] until the fetch lands, so the calendar and timeline drew
                  a complete empty grid and the list showed its "nothing
                  scheduled" empty state — which reads as "no auctions this
                  month" on a slow link, then flips to populated. `isLoading` is
                  first-load-only, so a background refetch still shows the
                  previous data dimmed rather than blanking the screen. Same
                  panel the legacy board uses (./components/legacy-board.tsx).
                */}
                {sessionsQuery.isLoading && (
                  <section className="overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-10 lg:px-[18px]">
                    <p className="text-center text-[13px] text-cf-ink-subtle">
                      {t('auctionSchedule.board.loading')}
                    </p>
                  </section>
                )}

                {!sessionsQuery.isLoading && screen === 'calendar' && (
                  <div className="grid grid-cols-[minmax(0,1fr)_360px] items-start gap-4">
                    <SessionCalendarView
                      cells={cells}
                      views={views}
                      today={today}
                      selected={selectedDay}
                      monthLabel={monthLabel}
                      weekdays={weekdays}
                      onSelect={handleSelectDay}
                      onPrevMonth={() => shiftMonth(-1)}
                      onNextMonth={() => shiftMonth(1)}
                      onToday={handleToday}
                    />

                    <aside className="sticky top-[35px] flex flex-col gap-3">
                      <SessionDayPanel
                        eyebrow={panelEyebrow}
                        title={formatPanelTitle(selectedDay)}
                        subtitle={panelSubtitle}
                        views={selectedViews}
                        nextHint={nextHint}
                        metaFor={(item) => `${lotsLabel(item.session.lots, t)} · ${statusLabel(item, today, t)}`}
                      />
                    </aside>
                  </div>
                )}

                {!sessionsQuery.isLoading && screen === 'list' && (
                  <SessionListView days={listDays} today={today} />
                )}

                {!sessionsQuery.isLoading && screen === 'timeline' && (
                  <SessionTimelineView
                    days={timelineDays}
                    today={today}
                    // Filtered, to match the calendar and the list: passing the
                    // full chip list left a blank row for every house the
                    // operator had just filtered out, since `cells` is built
                    // from `views`.
                    houses={timelineHouses}
                    cells={timelineCells}
                  />
                )}
              </div>
            </>
          )}
        </>
      )}
    </>
  );
}

/** Curried house-filter toggle, kept out of the component body for readability. */
function toggleHouse(setHouseFilter: (_updater: (_previous: string[]) => string[]) => void) {
  return (key: string) =>
    setHouseFilter((previous) => (previous.includes(key) ? previous.filter((item) => item !== key) : [...previous, key]));
}
